import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import SalesOfficeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity";
import { ICreateSalesOffice } from "./interfaces/create-sales-office.interface";
import { IUpdateSalesOffice } from "./interfaces/update-sales-office.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_CONFIGURATION_URL;
const moduleName = "sales-office";

@Injectable({
  providedIn: "root",
})
export class SalesOfficeService extends AbstractService<
  SalesOfficeAttributes,
  ICreateSalesOffice,
  IUpdateSalesOffice
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<SalesOfficeAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<SalesOfficeAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
    return this.http.get<IPaginationResult<SalesOfficeAttributes>>(API_URL, { headers });
  }

  /**
   * Obtiene oficinas de venta con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - uno busca en 'id', otro en 'name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: id, name
   * Array filters: statusId, companyId, salesOfficeGroupId
   */
  findAllWithCount(
    options: IPagination<SalesOfficeAttributes>,
    dataToFilter: IFilters,
  ): Observable<IPaginationResult<SalesOfficeAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, filtro de grupo, filtro de estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<SalesOfficeAttributes>[] => {
      const deletedFilter: FilterFn<SalesOfficeAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
          ? andWhere("company.id" as keyof SalesOfficeAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        dataToFilter?.salesOfficeGroupId
          ? andWhere("officeGroup.id" as keyof SalesOfficeAttributes, Ops.eq(dataToFilter.salesOfficeGroupId))
          : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
          ? andWhere("statusId", Ops.in(...dataToFilter.status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por ID (o solo condiciones si no hay búsqueda)
    const searchByIdQueryObject: FilterFn<SalesOfficeAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("id", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<SalesOfficeAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto, crea el OR)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<SalesOfficeAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByIdQueryObject,
      ...searchByNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;

    return this.http.get<IPaginationResult<SalesOfficeAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(companyId?: string): Observable<IRequestResult<SalesOfficeAttributes[]>> {
    const query = buildQuery<SalesOfficeAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      companyId ? where("company._id" as keyof SalesOfficeAttributes, Ops.eq(companyId)) : noop(),
      select(["id", "externalKeys", "name"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SalesOfficeAttributes[]>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data by group
   */
  findToComboBoxGroup(
    groupId?: string[]
  ): Observable<IRequestResult<SalesOfficeAttributes[]>> {
    const query = buildQuery<SalesOfficeAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      groupId ? andWhere(
        "officeGroup._id" as keyof SalesOfficeAttributes,
        Ops.in(...groupId)
      ) : noop(),
      select(["id", "name"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SalesOfficeAttributes[]>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data by group
   */
  findToComboBoxGroupByOneGroupId(
    groupId: string
  ): Observable<IRequestResult<SalesOfficeAttributes[]>> {
    const query = buildQuery<SalesOfficeAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andWhere(
        "officeGroup._id" as keyof SalesOfficeAttributes,
        groupId
      ),
      select(["id", "name"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SalesOfficeAttributes[]>>(API_URL);
  }
}
